import { useTranslation } from 'react-i18next';
import { AdminListing } from '@root/types';
import { Formik, Form } from 'formik';
import { FormThemeContextProvider } from '@components/shared/forms/ThemeContext';
import { SubmitButton } from '@components/shared/forms';
import Typography from '@styles/typography.module.less';
import classnames from 'classnames';
import validationSchema from './validationSchema';
import css from './styles.module.less';
import ListingDataCollectionList from './ListingDataCollectionList';
import listingSpaceMetadata from '../utils/listingSpaceMetadataBuilder';

type Props = {
  listing: AdminListing;
  updateListing: (
    listingId: string,
    body: {},
    message: string,
    successCallback?: () => void,
  ) => void;
};

const DataCollectionListingForm = ({ listing, updateListing }: Props) => {
  const { t } = useTranslation('admin');

  const initialValues = {
    ...listing,
    space_metadata: listingSpaceMetadata(listing),
  };

  return (
    <>
      <Formik
        enableReinitialize
        initialValues={JSON.parse(JSON.stringify(initialValues, (k, v) => (v === null ? '' : v)))}
        onSubmit={async (values, actions) => {
          const params = JSON.parse(JSON.stringify(values, (k, v) => (v === '' ? null : v)));
          await updateListing(listing.id, params, t('listing.updateSuccess'));
          actions.setSubmitting(false);
        }}
        validationSchema={validationSchema(t)}
      >
        {({ dirty, isValid, values }) => (
          <Form className={css.form}>
            <FormThemeContextProvider theme="vertical">
              <section className={css.dataCollection}>
                <div className={classnames(Typography.headerMediumSemibold)}>
                  {t('listing.dataCollection.title')}
                </div>
                <div className={css.dataCollection}>
                  <ListingDataCollectionList
                    buildOutLevel={values.space_metadata.buildOutLevel}
                    ceilingType={values.space_metadata.ceilingType}
                    coreFactorMultiTenantFloor={values.space_metadata.coreFactorMultiTenantFloor}
                    coreFactorSingleTenantFloor={values.space_metadata.coreFactorSingleTenantFloor}
                    currentFfe={values.space_metadata.currentFfe}
                    currentSpaceCondition={values.space_metadata.currentSpaceCondition}
                    drinkingFountainCount={values.space_metadata.drinkingFountainCount}
                    electricalRoom={values.space_metadata.electricalRoom}
                    elevatorLocation={values.space_metadata.elevatorLocation}
                    exposedColumns={values.space_metadata.exposedColumns}
                    exposureEast={values.space_metadata.exposureEast}
                    exposureNorth={values.space_metadata.exposureNorth}
                    exposureSouth={values.space_metadata.exposureSouth}
                    exposureWest={values.space_metadata.exposureWest}
                    exteriorSignage={values.space_metadata.exteriorSignage}
                    floorType={values.space_metadata.floorType}
                    gym={values.space_metadata.gym}
                    heatType={values.space_metadata.heatType}
                    interiorSignage={values.space_metadata.interiorSignage}
                    mainPantryKitchen={values.space_metadata.mainPantryKitchen}
                    mechanicalRoom={values.space_metadata.mechanicalRoom}
                    officeFronts={values.space_metadata.officeFronts}
                    officeLayout={values.space_metadata.officeLayout}
                    privateBathroomsShower={values.space_metadata.privateBathroomsShower}
                    receptionCeilingType={values.space_metadata.receptionCeilingType}
                    receptionFloorType={values.space_metadata.receptionFloorType}
                    serverRoom={values.space_metadata.serverRoom}
                    serviceType={values.space_metadata.serviceType}
                    sharedFloorBathroomStallCount={
                      values.space_metadata.sharedFloorBathroomStallCount
                    }
                    usableSquareFeet={values.space_metadata.usableSquareFeet}
                    viewLandmarkDetail={values.space_metadata.viewLandmarkDetail}
                    windowSidesCount={values.space_metadata.windowSidesCount}
                    windowViewEast={values.space_metadata.windowViewEast}
                    windowViewNorth={values.space_metadata.windowViewNorth}
                    windowViewSouth={values.space_metadata.windowViewSouth}
                    windowViewWest={values.space_metadata.windowViewWest}
                    windowViews={values.space_metadata.windowViews}
                  />
                </div>
              </section>
              {dirty ? (
                <div className={css.submitFooter}>
                  <SubmitButton disabled={!isValid}>{t('common:saveChanges')}</SubmitButton>
                </div>
              ) : null}
            </FormThemeContextProvider>
          </Form>
        )}
      </Formik>
    </>
  );
};

export default DataCollectionListingForm;
